import { UnexpectedValueError } from "@anna-money/anna-web-lib/utils";
import type { AbnFormValues, GstFormValues } from "helpers/forms/acn/abnTypes";
import { CompanyStore } from "services/company/companyStore";
import { GstLodgementFrequencyOptions, GstTurnoverOptions } from "services/company/companyTypes";
import type { GstCompanyStore } from "services/company/gstCompanyStore";
import type { CompanyBusinessActivitiesStore } from "services/companyBusinessActivities/au/companyBusinessActivitiesStore";
import { FormError } from "types/errors";

export enum AbnStepEntity {
  BusinessDescription = "BusinessDescription",
  BusinessAddress = "BusinessAddress",
  BusinessActivity = "BusinessActivity",
  BusinessJurisdiction = "BusinessJurisdiction",
  BusinessGST = "BusinessGST",
  GSTTurnover = "GSTTurnover",
  AgriculturalOperations = "AgriculturalOperations",
}

export class AbnStepStore {
  constructor(
    private readonly _companyStore: CompanyStore,
    private readonly _companyBusinessActivitiesStore: CompanyBusinessActivitiesStore,
    private readonly _gstCompanyStore: GstCompanyStore,
  ) {}

  checkAllAbnDetailsExist(): boolean {
    const company = this._companyStore.company;
    if (!company) {
      return false;
    }

    // Check business description
    if (!company.natureOfBusiness) {
      return false;
    }

    // Check business address
    if (!this._companyBusinessActivitiesStore.address) {
      return false;
    }

    // Check main business activity
    if (!company.businessActivityCode) {
      return false;
    }

    // Check state of registration
    if (!company.stateOfRegistration) {
      return false;
    }

    // Check agricultural operations flag is set (can be true or false)
    if (company.isConductAgriculturalPropertyOperations === undefined) {
      return false;
    }

    return true;
  }

  async updateEntity(data: AbnFormValues, entity: AbnStepEntity): Promise<void> {
    switch (entity) {
      case AbnStepEntity.BusinessAddress:
        await this._updateBusinessAddress(data);
        break;
      case AbnStepEntity.BusinessActivity:
        await this._updateBusinessActivity(data);
        break;
      case AbnStepEntity.AgriculturalOperations:
        await this._updateAgriculturalOperations(data);
        break;
      case AbnStepEntity.BusinessJurisdiction:
        await this._updateBusinessJurisdiction(data);
        break;
      case AbnStepEntity.BusinessGST:
      case AbnStepEntity.GSTTurnover:
        await this._updateBusinessGST(data);
        break;
      case AbnStepEntity.BusinessDescription:
        await this._updateBusinessDescription(data);
        break;
      default:
        throw new UnexpectedValueError("entity", entity);
    }
  }

  private async _updateBusinessJurisdiction(data: AbnFormValues): Promise<void> {
    if (typeof data.stateOfRegistration === "undefined") {
      throw new FormError("No state jurisdiction value");
    }

    await this._companyStore.updateCompanyStateOfRegistration(data.stateOfRegistration);
  }

  private async _updateAgriculturalOperations(data: AbnFormValues): Promise<void> {
    if (typeof data.isConductAgriculturalPropertyOperations === "undefined") {
      throw new FormError("No agricultural operations value");
    }

    await this._companyStore.updateAgriculturalPropertyOperations(data.isConductAgriculturalPropertyOperations);
  }

  private async _updateBusinessAddress(data: AbnFormValues): Promise<void> {
    if (!data.businessAddress) {
      throw new FormError("No address selected");
    }

    await this._companyBusinessActivitiesStore.updateAddress(data.businessAddress);
  }

  private async _updateBusinessActivity(data: AbnFormValues): Promise<void> {
    if (!data.businessActivities?.length) {
      throw new FormError("No business activities value");
    }

    const mainBusinessActivity = data.businessActivities[0];

    if (!mainBusinessActivity?.code) {
      throw new FormError("No business activity code value");
    }

    if (!data.businessAddress) {
      throw new FormError("No business activity address value");
    }

    await this._companyBusinessActivitiesStore.updateActivity(
      mainBusinessActivity.code,
      mainBusinessActivity.description,
      data.businessAddress,
    );
  }

  private async _updateBusinessDescription(data: AbnFormValues): Promise<void> {
    await this._companyStore.updateAbnBusinessDescription(data.natureOfBusiness || "");
  }

  private async _updateBusinessGST(data: AbnFormValues | GstFormValues): Promise<void> {
    const gstData: AbnFormValues | GstFormValues = { ...data };

    // Transform gstTurnover from enum key to enum value
    if (data.gstTurnover && typeof data.gstTurnover === "string") {
      const turnoverKey = data.gstTurnover as keyof typeof GstTurnoverOptions;
      if (turnoverKey in GstTurnoverOptions) {
        gstData.gstTurnover = GstTurnoverOptions[turnoverKey];
      }
    }

    // Transform gstLodgementFrequency from enum key to enum value
    if (data.gstLodgementFrequency && typeof data.gstLodgementFrequency === "string") {
      const frequencyKey = data.gstLodgementFrequency as keyof typeof GstLodgementFrequencyOptions;
      if (frequencyKey in GstLodgementFrequencyOptions) {
        gstData.gstLodgementFrequency = GstLodgementFrequencyOptions[frequencyKey];
      }
    }

    await this._gstCompanyStore.updateGST(gstData as GstFormValues);
  }
}
